// ------------------------------------------------------------
// FamilyStep.tsx – dynamic member forms
// ------------------------------------------------------------
import React, { useEffect, useState } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { Box, TextField, Button, Typography, FormControlLabel, FormGroup, Checkbox, CircularProgress, Alert, Chip, Paper } from "@mui/material";
import { Add } from "@mui/icons-material";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { fetchTags } from "./TagService";

type Tag = { tag_id: number; tag_name: string };

// put this near the top of the component (outside the map)
const mealTypes = ["breakfast", "lunch", "dinner"] as const;
type MealType = typeof mealTypes[number];

const days = ["monday","tuesday","wednesday","thursday","friday","saturday","sunday"] as const; // Use const assertion for better type inference

const mealScheduleSchema = z.object({
  breakfast: z.array(z.enum(days)),
  lunch:     z.array(z.enum(days)),
  dinner:    z.array(z.enum(days))
});

const schema = z.object({
  members: z.array(
    z.object({
      name: z.string().min(1, "Name required"),
      isChild: z.boolean(),
      restrictionTagIds: z.array(z.number()),
      medicalTagIds: z.array(z.number()),
      allergyTagIds: z.array(z.number()),
      otherAllergies: z.array(z.string()),
      ingredientDislikes: z.array(z.string()),
      mealSchedule: mealScheduleSchema,
    })
  ).min(1),
});

type FormData = z.infer<typeof schema>;

type Props = { onBack: () => void; onNext: (data: FormData) => void };
const FamilyStep: React.FC<Props> = ({ onBack, onNext }) => {
  const [restrictions, setRestrictions] = useState<Tag[]>([]);
  const [medical, setMedical] = useState<Tag[]>([]);
  const [allergies, setAllergies] = useState<Tag[]>([]);

  const { control, handleSubmit, watch, formState, reset, setValue, getValues } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      members: [{
        name: "",
        isChild: false,
        restrictionTagIds: [],
        medicalTagIds: [],
        allergyTagIds: [],
        otherAllergies: [],
        ingredientDislikes: [],
        mealSchedule: { breakfast: [], lunch: [], dinner: [] }
      }]
    },
  });

  // State for managing input fields for allergies and dislikes
  const [newAllergyInputs, setNewAllergyInputs] = useState<{ [key: number]: string }>({});
  const [newDislikeInputs, setNewDislikeInputs] = useState<{ [key: number]: string }>({});
  const { fields, append, remove } = useFieldArray({ control, name: "members" });

  useEffect(() => {
    const load = async () => {
      const res = await fetchTags("restriction");
      setRestrictions(res);
      setMedical(await fetchTags("medical"));
      setAllergies(await fetchTags("allergy"));

      // 👇 Only reset once to avoid wiping user input
      if (res.length) {
        reset({
          members: [
            {
              name: "",
              isChild: false,
              restrictionTagIds: [],
              medicalTagIds: [],
              allergyTagIds: [],
              otherAllergies: [],
              ingredientDislikes: [],
              mealSchedule: { breakfast: [], lunch: [], dinner: [] }
            },
          ],
        });
      }
    };
    load();
  }, [reset]);

  if (!restrictions.length) return <CircularProgress />;

const submit = (data: FormData) => {
  console.log("✅ FamilyStep submit payload", data);
  onNext(data);                 // <- original callback
};

console.log("🪲 RHF errors ➜", formState.errors);
// ↑ Put this right after const { control, handleSubmit, watch, formState } = useForm ...

return (
  <form onSubmit={handleSubmit(submit)}>
      {fields.map((field, idx) => (
        <Box key={field.id} sx={{ mb: 4, p: 2, border: '1px solid #ccc', borderRadius: 2 }}>
          <Typography variant="h6">Family member {idx + 1}</Typography>

          <Controller
            name={`members.${idx}.name` as const}
            control={control}
            render={({ field }) => (
              <TextField
                label="Name"
                fullWidth
                margin="normal"
                {...field}
                error={!!formState.errors.members?.[idx]?.name}
                helperText={formState.errors.members?.[idx]?.name?.message}
              />
            )}
          />

          {/* Kid-Friendly Checkbox - Prominent placement */}
          <Controller
            name={`members.${idx}.isChild` as const}
            control={control}
            render={({ field }) => (
              <Paper
                sx={{
                  p: 2,
                  mt: 2,
                  mb: 2,
                  bgcolor: field.value ? '#FFF8E7' : '#FAFAFA',
                  border: field.value ? '2px solid #FFD700' : '1px solid #E0E0E0',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#FFD700',
                    bgcolor: '#FFFDF5',
                  },
                }}
                onClick={() => field.onChange(!field.value)}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      sx={{
                        color: '#FFD700',
                        '&.Mui-checked': {
                          color: '#FFD700',
                        },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontWeight: 600, color: '#333' }}>
                        Kid-Friendly Meals
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Generate simpler, more familiar meals for children
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0, width: '100%' }}
                />
              </Paper>
            )}
          />

            {/* Restriction checkboxes (multi-select) */}
          <Typography sx={{ mt: 2 }}>Dietary restrictions</Typography>
          <Controller
            name={`members.${idx}.restrictionTagIds` as const}
            control={control}
            render={({ field }) => (
              <FormGroup row>
                {restrictions.map((tag) => (
                  <FormControlLabel
                    key={tag.tag_id}
                    control={
                      <Checkbox
                        checked={field.value?.includes(tag.tag_id) ?? false}
                        onChange={(e) => {
                          const current = field.value || [];
                          field.onChange(
                            e.target.checked
                              ? [...current, tag.tag_id]
                              : current.filter((id: number) => id !== tag.tag_id)
                          );
                        }}
                      />
                    }
                    label={tag.tag_name}
                  />
                ))}
              </FormGroup>
            )}
          />


          {/* Medical checkboxes */}
          <Typography sx={{ mt: 2 }}>Medical nutrition needs</Typography>
          <Controller
            name={`members.${idx}.medicalTagIds` as const}
            control={control}
            render={({ field }) => (
              <FormGroup row>
                {medical.map((tag) => (
                  <FormControlLabel key={tag.tag_id} control={<Checkbox checked={field.value.includes(tag.tag_id)} onChange={(e) => {
                      const checked = e.target.checked;
                      field.onChange(checked ? [...field.value, tag.tag_id] : field.value.filter((id) => id !== tag.tag_id));
                    }} />} label={tag.tag_name} />
                ))}
              </FormGroup>
            )}
          />

          {/* Allergy checkboxes */}
          <Typography sx={{ mt: 2 }}>Allergies</Typography>
          <Controller
            name={`members.${idx}.allergyTagIds` as const}
            control={control}
            render={({ field }) => (
              <FormGroup row>
                {allergies.map((tag) => (
                  <FormControlLabel key={tag.tag_id} control={<Checkbox checked={field.value.includes(tag.tag_id)} onChange={(e) => {
                      const checked = e.target.checked;
                      field.onChange(checked ? [...field.value, tag.tag_id] : field.value.filter((id) => id !== tag.tag_id));
                    }} />} label={tag.tag_name} />
                ))}
              </FormGroup>
            )}
          />

          {/* Other Allergies */}
          <Paper sx={{ p: 2, mt: 2, bgcolor: '#fff5f5', border: '1px solid #ffcdd2' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: '#c62828' }}>
              Other Allergies (not listed above)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Add any allergies not in the list above - we'll strictly avoid these ingredients
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                size="small"
                placeholder="e.g., sesame, shellfish..."
                value={newAllergyInputs[idx] || ''}
                onChange={(e) => setNewAllergyInputs(prev => ({ ...prev, [idx]: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const value = (newAllergyInputs[idx] || '').trim().toLowerCase();
                    const current = getValues(`members.${idx}.otherAllergies`);
                    if (value && !current.includes(value)) {
                      setValue(`members.${idx}.otherAllergies`, [...current, value]);
                      setNewAllergyInputs(prev => ({ ...prev, [idx]: '' }));
                    }
                  }
                }}
                sx={{ flex: 1 }}
              />
              <Button
                size="small"
                variant="contained"
                sx={{ bgcolor: '#c62828', '&:hover': { bgcolor: '#b71c1c' } }}
                disabled={!(newAllergyInputs[idx] || '').trim()}
                onClick={() => {
                  const value = (newAllergyInputs[idx] || '').trim().toLowerCase();
                  const current = getValues(`members.${idx}.otherAllergies`);
                  if (value && !current.includes(value)) {
                    setValue(`members.${idx}.otherAllergies`, [...current, value]);
                    setNewAllergyInputs(prev => ({ ...prev, [idx]: '' }));
                  }
                }}
              >
                <Add fontSize="small" />
              </Button>
            </Box>
            {watch(`members.${idx}.otherAllergies`).length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {watch(`members.${idx}.otherAllergies`).map((allergy, i) => (
                  <Chip
                    key={i}
                    label={allergy}
                    size="small"
                    color="error"
                    onDelete={() => {
                      const current = getValues(`members.${idx}.otherAllergies`);
                      setValue(`members.${idx}.otherAllergies`, current.filter(a => a !== allergy));
                    }}
                  />
                ))}
              </Box>
            )}
          </Paper>

          {/* Allergy warning - show when any allergies are selected */}
          {(watch(`members.${idx}.allergyTagIds`).length > 0 || watch(`members.${idx}.otherAllergies`).length > 0) && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <strong>Important:</strong> While we check all recipes against your allergy restrictions, AI can make mistakes. Please always verify ingredients before cooking. When in doubt, make substitutions.
            </Alert>
          )}

          {/* Meal schedule */}
          <Typography sx={{ mt: 2 }}><b>Meals per week</b></Typography>

          {mealTypes.map((meal: MealType) => (
            <Box key={meal} sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ textTransform: "capitalize" }}>
                  {meal}
                </Typography>
                <Controller
                  name={`members.${idx}.mealSchedule.${meal}` as const}
                  control={control}
                  render={({ field }) => {
                    const value: string[] = Array.isArray(field.value) ? field.value : [];
                    const allSelected = days.every(d => value.includes(d));
                    return (
                      <Button
                        size="small"
                        variant={allSelected ? "outlined" : "contained"}
                        sx={{
                          fontSize: '0.7rem',
                          minWidth: 'auto',
                          py: 0.5,
                          px: 1.5,
                          backgroundColor: allSelected ? 'transparent' : '#688E26',
                          color: allSelected ? '#688E26' : '#fff',
                          borderColor: '#688E26',
                          '&:hover': {
                            backgroundColor: allSelected ? 'rgba(104, 142, 38, 0.08)' : '#567522',
                            borderColor: '#688E26',
                          },
                        }}
                        onClick={() => field.onChange(allSelected ? [] : [...days])}
                      >
                        {allSelected ? 'Deselect All' : 'Select All'}
                      </Button>
                    );
                  }}
                />
              </Box>

              <Controller
                name={`members.${idx}.mealSchedule.${meal}` as const}
                control={control}
                render={({ field }) => {
                  const value: string[] = Array.isArray(field.value) ? field.value : [];  // 🛡 guard
                  return (
                    <FormGroup row>
                      {days.map((d) => (
                        <FormControlLabel
                          key={d}
                          control={
                            <Checkbox
                              size="small"
                              checked={value.includes(d)}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                field.onChange(
                                  checked ? [...value, d] : value.filter((day) => day !== d)
                                );
                              }}
                            />
                          }
                          label={d.slice(0, 3)} // Mon, Tue …
                        />
                      ))}
                    </FormGroup>
                  );
                }}
              />
            </Box>
          ))}

          {/* Ingredient Dislikes */}
          <Paper sx={{ p: 2, mt: 3, bgcolor: '#f9f9f9' }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Ingredient Dislikes (Optional)
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Add ingredients this person won't eat - we'll avoid them in meal plans
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                size="small"
                placeholder="e.g., mushrooms, olives..."
                value={newDislikeInputs[idx] || ''}
                onChange={(e) => setNewDislikeInputs(prev => ({ ...prev, [idx]: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const value = (newDislikeInputs[idx] || '').trim().toLowerCase();
                    const current = getValues(`members.${idx}.ingredientDislikes`);
                    if (value && !current.includes(value)) {
                      setValue(`members.${idx}.ingredientDislikes`, [...current, value]);
                      setNewDislikeInputs(prev => ({ ...prev, [idx]: '' }));
                    }
                  }
                }}
                sx={{ flex: 1 }}
              />
              <Button
                size="small"
                variant="outlined"
                disabled={!(newDislikeInputs[idx] || '').trim()}
                onClick={() => {
                  const value = (newDislikeInputs[idx] || '').trim().toLowerCase();
                  const current = getValues(`members.${idx}.ingredientDislikes`);
                  if (value && !current.includes(value)) {
                    setValue(`members.${idx}.ingredientDislikes`, [...current, value]);
                    setNewDislikeInputs(prev => ({ ...prev, [idx]: '' }));
                  }
                }}
              >
                <Add fontSize="small" />
              </Button>
            </Box>
            {watch(`members.${idx}.ingredientDislikes`).length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {watch(`members.${idx}.ingredientDislikes`).map((ingredient, i) => (
                  <Chip
                    key={i}
                    label={ingredient}
                    size="small"
                    variant="outlined"
                    onDelete={() => {
                      const current = getValues(`members.${idx}.ingredientDislikes`);
                      setValue(`members.${idx}.ingredientDislikes`, current.filter(d => d !== ingredient));
                    }}
                  />
                ))}
              </Box>
            )}
          </Paper>

          {fields.length > 1 && (
            <Button color="error" sx={{ mt: 2 }} onClick={() => remove(idx)}>Remove</Button>
          )}
        </Box>
      ))}

      <Button variant="outlined" onClick={() => append({
        name: "",
        isChild: false,
        restrictionTagIds: [],
        medicalTagIds: [],
        allergyTagIds: [],
        otherAllergies: [],
        ingredientDislikes: [],
        mealSchedule: { breakfast: [], lunch: [], dinner: [] }
      })}>
        Add another person
      </Button>

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={onBack}>Back</Button>
        <Button variant="contained" type="submit">Next</Button>
      </Box>
    </form>
  );
};
export default FamilyStep;